/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";

import { useCallback, useEffect, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { debounce } from "lodash";
import useResponse from "@/shared/hooks/useResponse";
import { useDetailsStore } from "@/stores/useDetailsStore";
import { useTableStoreByRoute } from "@/stores/useTableStore";

import { SearchIcon } from "@dotnet/telematics-design-system";

import useAxiosPrivate from "../../api/hooks/useAxiosPrivate";
import {
  filterDropdownCustomWidth,
  INPUT_LENGTH,
  ROUTES,
  TABLE_NAMES,
  useMediaQueries
} from "../../shared";
import RectangleLoader from "../../shared/components/loaders/RectangleLoader";
import { removeSlashFromPath } from "../../shared/functions/functions";
import { ClearInputIcon } from "../InputField/components/ClearInputIcon";
import InputField from "../InputField/InputField";
import Tooltip from "../Tooltip/Tooltip";
import { MIN_CHAR_LENGTH_IN_SEARCH } from "./constants";
import { useStyles } from "./styles";

interface SearchProps {
  route: ROUTES;
  stretchSearch?: boolean;
}

const Search = ({ route, stretchSearch }: SearchProps) => {
  const { t } = useTranslation();
  const { getData } = useAxiosPrivate();
  const { handleResponse } = useResponse();
  const { toMd } = useMediaQueries();

  const {
    params: { contains },
    initialLoading,
    setParams,
    fetchData
  } = useTableStoreByRoute(route);
  const { resetDetailsSection } = useDetailsStore();

  const isInitialLoading = initialLoading || resetDetailsSection;
  const searchParam = contains;
  const [value, setValue] = useState<string>(searchParam ?? "");

  const { inputField, searchInputSpan } = useStyles(stretchSearch);

  const getSearchByValue = () => {
    if (route === ROUTES.UsersInvitations) {
      return TABLE_NAMES.Users;
    }

    if (
      route === ROUTES.IntegrationsApi ||
      route === ROUTES.AnalysisProviders
    ) {
      return TABLE_NAMES.Integrations;
    }

    return removeSlashFromPath(route);
  };

  const searchByValue = getSearchByValue();

  // Sync input when store changes externally (reset, etc.)
  useEffect(() => {
    const storeValue = searchParam ?? "";
    if (storeValue !== value) {
      setValue(storeValue);
    }
  }, [searchParam]);

  const performSearch = useCallback(
    async (searchValue: string) => {
      const searchParamValue =
        searchValue.length >= MIN_CHAR_LENGTH_IN_SEARCH ? searchValue : null;

      // Only update if something actually changed
      if (searchParamValue === searchParam) return;

      setParams({
        contains: searchParamValue,
        pageNumber: "1"
      });

      await fetchData(getData, handleResponse);
    },
    [fetchData, getData, searchParam, setParams]
  );

  const debouncedSearch = useMemo(
    () => debounce(performSearch, 500),
    [performSearch]
  );

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  // Trigger debounced search only when user types (not when params changes)
  useEffect(() => {
    if (isInitialLoading) return;

    const trimmedValue = value.trim();

    const shouldSearch =
      trimmedValue.length >= MIN_CHAR_LENGTH_IN_SEARCH ||
      trimmedValue.length === 0;

    if (shouldSearch) {
      debouncedSearch(trimmedValue);
    }
  }, [value, debouncedSearch, isInitialLoading]);

  const changeValue = (e: { target: { value: string } }) => {
    const newValue = e.target.value;
    const isNotTooLong = newValue.length <= INPUT_LENGTH.MaxValueInSearchField;
    if (isNotTooLong) setValue(newValue);
  };

  const clearValue = async () => {
    setValue("");
    debouncedSearch.cancel();

    if (searchParam !== null) {
      setParams({
        contains: null,
        pageNumber: "1"
      });
      await fetchData(getData, handleResponse);
    }
  };

  return (
    <>
      {isInitialLoading && (
        <RectangleLoader
          customStyle={filterDropdownCustomWidth}
          height={36}
          testId="search-loader"
        />
      )}
      {!isInitialLoading && (
        <Tooltip
          title={t(`Search##search in ${searchByValue}`)}
          size="medium"
          placement={toMd ? "top" : "right"}
          disableInteractive
        >
          <span css={css(searchInputSpan)}>
            <InputField
              fullWidth
              size="small"
              value={value}
              onChange={changeValue}
              iconLeft={<SearchIcon />}
              iconRight={
                <ClearInputIcon value={value} clearValue={clearValue} />
              }
              placeholder={t("Search##search")}
              css={css(inputField)}
            />
          </span>
        </Tooltip>
      )}
    </>
  );
};

export default Search;
